import { Button } from "@/components/ui/button";
import { ArrowRight, Download } from "lucide-react";
import { generateProposalPDF } from "@/lib/pdfGenerator";

const pillars = [
  {
    title: "Initial Case Worker Hires",
    body: "CDBG seed funding for 4 full-time service connectors, each carrying ~25 active households (75% senior / 25% family blend on Year One).",
  },
  {
    title: "Intake & Tracking Infrastructure",
    body: "Standardized intake matrix, secure case management system, and quarterly ROI reporting back to City Council and HUD.",
  },
  {
    title: "Coordinated Referral Protocol",
    body: "Formal MOUs with Lee College, Baytown Housing Authority, Harris Health, Workforce Solutions, and Harris County Transit.",
  },
  {
    title: "Two-Year Outcomes Horizon",
    body: "Each cohort tracked from intake to thriving baseline across stability domains — measurable, fundable, replicable.",
  },
];

export default function MunicipalPartnership() {
  return (
    <section
      id="partnership"
      data-testid="section-partnership"
      className="bg-[#F9F7F1] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-8 bg-[#8B0000]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
                08 — Municipal Partnership Framework
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              The CDBG ask.<br />
              <em className="italic">A non-competing multiplier of your existing investments.</em>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:pt-2 text-[#334155] leading-relaxed">
            <p>
              The City of Baytown is a direct HUD entitlement jurisdiction. The active
              CDBG funding cycle explicitly seeks non-profit and public-service
              partners to support low- and moderate-income residents. The Baytown
              Bridge requests a CDBG public-service sub-recipient award — and brings
              in matching philanthropic support — to launch as a permanent agency
              embedded in the Baytown service ecosystem.
            </p>
            <p className="mt-4">
              We multiply the City's existing investments rather than competing with
              them. Every dollar funds a connector who in turn unlocks Lee College
              enrollment, Section 8 utilization, Medicaid sign-ups, and LIHEAP draw —
              dollars already allocated, finally activated.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1] mb-12">
          {pillars.map((p, i) => (
            <div
              key={p.title}
              data-testid={`partnership-pillar-${i}`}
              className="bg-white p-6 lg:p-7"
            >
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#8B0000] mb-3">
                Pillar 0{i + 1}
              </div>
              <h3 className="font-display text-xl text-[#0A192F] leading-snug">{p.title}</h3>
              <p className="mt-3 text-sm text-[#475569] leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>

        <div className="bg-[#0A192F] text-[#F9F7F1] p-8 lg:p-12 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#D4AF37] mb-2">
              The Ask
            </div>
            <h3 className="font-display text-2xl lg:text-4xl leading-tight">
              Fund the bridge. Activate the city you already built.
            </h3>
            <p className="mt-4 text-[#CBD5E1] leading-relaxed max-w-2xl">
              We are ready to brief the Community Development Division, the Baytown
              Housing Authority Board, and City Council. The model is turnkey. The
              timing aligns with the upcoming Consolidated Plan cycle.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Button
              data-testid="partnership-schedule-cta"
              asChild
              className="rounded-none bg-[#8B0000] hover:bg-[#A41E1E] text-[#F9F7F1] h-12 font-mono text-xs uppercase tracking-widest"
            >
              <a href="#contact">
                Schedule a Briefing <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button
              data-testid="partnership-download-cta"
              onClick={generateProposalPDF}
              variant="outline"
              className="rounded-none border-[#F9F7F1] bg-transparent text-[#F9F7F1] hover:bg-[#F9F7F1] hover:text-[#0A192F] h-12 font-mono text-xs uppercase tracking-widest"
            >
              <Download className="mr-2 h-4 w-4" /> Download Proposal PDF
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
